import { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import PropTypes from "prop-types"
import classnames from "classnames"
import { map } from "lodash"
import { CardContent, Paper, Tooltip as MuiTooltip } from "@mui/material"
import { OfflineBolt } from "@mui/icons-material"
import { DATE } from "helpers/shapes"
import { computeCSVData } from "helpers/csv"
import { useTrack } from "helpers/analytics"
import { smallPercentageFormatter } from "helpers/formatters/numbers"
import MultiLine from "components/charts/multi-line"
import Tooltip from "components/analyze-tooltip"
import NoData from "components/no-data"
import ForecastDetails from "modules/analyze-composite-forecast-details"
import GraphTopBar from "components/graph-top-bar"
import ExcelDownload from "components/excel-download"
import { VIEW_TYPES } from "helpers/base"
import "./style.scss"

export default function AnalyzeCompositeForecast({
  analyzeViewSelectList,
  account,
  modelId,
  positionSetId,
  positionSetType,
  positionSetName,
  forecastId,
  forecast,
  dailyForecastData,
  earliestDate,
  latestDate,
  setDate,
  selectedDate,
  startDate,
  endDate,
  factorCategoryId,
  factorCategoryName,
  selectFactorCategory,
  segment,
  data,
  classifications,
  allCategoryItems,
  currentGrouping,
  setGrouping,
  items,
  options,
  toggle,
  toggleOnly,
  focusChartLine,
  blurChartLine,
  focusedChartLine,
  viewType,
  fileName,
  firstDate,
  selectedOption,
  only,
  filtersVisible,
}) {
  const trackExcelDownload = useTrack({
    type: "composite-analyze-forecast-graph-timeseries-download",
  })

  const ACTIVE_TOOLTIP_MSG = `
    Although a comparison is currently provided,
    this portfolio's expected return is displayed in absolute terms.`

  const onToggleOnly = (factorId) => {
    toggleOnly(factorId, forecastId)
  }

  const navigate = useNavigate()

  useEffect(() => {
    if (!forecast) {
      navigate(`/${account}/composite/${positionSetType}/${positionSetId}/trends/performance`)
    }
  }, [account, positionSetType, positionSetId, forecast, navigate])

  let graph, rightPanel

  if (!earliestDate || !latestDate) {
    graph = <NoData title="Forecasts" className="graph" />
  }
  if (!data.length) {
    graph = <div className="components_charts_multi-line no-data">No securities in this portfolio was found in this forecast.</div>
  } else {
    graph = <MultiLine
      tooltip={<Tooltip data={data} items={items} focusedChartLine={focusedChartLine} formatter={smallPercentageFormatter} />}
      {...{
        tickFormat: smallPercentageFormatter,
        startDate,
        date: selectedDate,
        setDate,
        endDate,
        data,
        onlyItem: only,
        items,
        toggle,
        focusChartLine,
        blurChartLine,
        focusedChartLine,
        toggleOnly: onToggleOnly,
      }}
    />
    rightPanel = <ForecastDetails {...{
      account,
      modelId,
      positionSetId,
      positionSetType,
      positionSetName,
      forecastId,
      dailyForecastData,
      classifications,
      allCategoryItems,
      selectedDate,
      viewType,
      factorCategoryId,
      factorCategoryName,
      startDate: firstDate,
      endDate,
      selectedOption,
      setGrouping,
      currentGrouping,
      segment,
      filtersVisible,
    }}
    />
  }

  return <div className="modules_analyze-composite-forecast">
    <div className="historical-forecast-wrapper">
      <CardContent className={classnames("card-content")}>
        <Paper className="content">
          <div className="graph-container">
            <div className="graph-top">
              {analyzeViewSelectList}
              <GraphTopBar {...{
                value: factorCategoryId,
                onTabChanged: (_, factorId) => selectFactorCategory(factorId),
                tabs: map(options, ({ id, label }) => ({
                  key: id,
                  value: id,
                  label,
                  tooltip: viewType === VIEW_TYPES.ACTIVE
                    ? <MuiTooltip title={ACTIVE_TOOLTIP_MSG}>
                      <OfflineBolt className="warning" />
                    </MuiTooltip> : null,
                })),
                actions: [<ExcelDownload key="excel-download" data={computeCSVData(data, items, { percents: true })} fileName={fileName} track={trackExcelDownload} />],
              }}
              />
            </div>
            <div className="graph">
              {graph}
            </div>
          </div>
          <div className="contributors">
            {rightPanel}
          </div>
        </Paper>
      </CardContent>
    </div>
  </div>
}

AnalyzeCompositeForecast.propTypes = {
  analyzeViewSelectList: PropTypes.element,
  account: PropTypes.string.isRequired,
  modelId: PropTypes.string.isRequired,
  positionSetId: PropTypes.string.isRequired,
  positionSetType: PropTypes.string.isRequired,
  positionSetName: PropTypes.string.isRequired,
  forecastId: PropTypes.string.isRequired,
  forecast: PropTypes.object,
  dailyForecastData: PropTypes.array.isRequired,
  factorCategoryId: PropTypes.string.isRequired,
  factorCategoryName: PropTypes.string.isRequired,
  selectFactorCategory: PropTypes.func.isRequired,
  allCategoryItems: PropTypes.array.isRequired,
  earliestDate: DATE,
  latestDate: DATE,
  startDate: DATE,
  endDate: DATE,
  selectedDate: DATE,
  segment: PropTypes.object,
  data: PropTypes.array.isRequired,
  classifications: PropTypes.array,
  currentGrouping: PropTypes.object,
  setGrouping: PropTypes.func.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    disabled: PropTypes.bool.isRequired,
  })).isRequired,
  viewType: PropTypes.string.isRequired,
  setDate: PropTypes.func.isRequired,
  only: PropTypes.string,
  toggle: PropTypes.func.isRequired,
  toggleOnly: PropTypes.func.isRequired,
  focusChartLine: PropTypes.func.isRequired,
  blurChartLine: PropTypes.func.isRequired,
  addNotification: PropTypes.func.isRequired,
  focusedChartLine: PropTypes.string,
  fileName: PropTypes.string.isRequired,
  firstDate: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
  })).isRequired,
  selectedOption: PropTypes.object.isRequired,
  filtersVisible: PropTypes.bool.isRequired,
}
